<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import {
  WidgetBankPageAboutBank,
  WidgetBankPageDetails,
  WidgetBankPageShowOnMap,
  WidgetBankPageRatesChartNbkr,
  getBankDetailsList
} from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import { FeatureBankPageFormSupport } from '~/components/feature/bank-page/form-support'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetDepositsList } from '~/components/widget/deposits'
import { WidgetCreditsList } from '~/components/widget/credits'
import { WidgetSelectCreditType } from '~/components/widget/select-credit-type'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetRatesNbkr } from '~/components/widget/rates'
import { WidgetCardsList } from '~/components/widget/cards'
import { WidgetSidebarRates } from '~/components/widget/sidebar-rates'
import { WidgetSidebarGoldPrice } from '~/components/widget/sidebar-gold-price'
import { WidgetNewsSlider } from '~/components/widget/news-slider'
import { WidgetStaff } from '~/components/widget/staff'
import { FeatureCreditFormRequest } from '~/components/feature/credit/form-request'
import { ContentBlock } from '~/components/ui/content-block'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetApplications } from '~/components/widget/applications'
import { WidgetComments } from '~/components/widget/comments'
import { WidgetRateAlertSidebar } from '~/components/widget/rate-alert-sidebar'
import type { IOrganizationRateData } from '~/components/feature/rate-alert'

const { t, locale } = useI18n()
const route = useRoute()
const url = `/banks/${route.params.slug}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => url, {
  key: `banks-page-${route.params.slug}`
})
const bank = computed(() => data.value?.data)
const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: bank.value?.title || t('common.bank'), url: bank.value?.url }
])
const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl

useSeoFromEntity(bank, baseUrl, locale.value)

const { data: ratesData } = await useFetch('/api/rates/organizations-cached', {
  query: {
    lang: locale.value || 'ru'
  }
})

const allOrganizationsRates = computed<IOrganizationRateData[]>(() => {
  return (
    ratesData.value?.data.map((item) => ({
      id: item.id,
      title: item.title,
      rates: item.rates
    })) || []
  )
})

const hasCredits = ref<boolean | null>(null)
const hasDeposits = ref<boolean | null>(null)
const hasCards = ref<boolean | null>(null)

const isTabsInitialized = ref(false)
const activeTab = ref('credits')

const tabs = computed(() => [
  {
    value: 'credits',
    label: t('common.credits'),
    content: t('page.bank.slug.tabs_content.credits')
  },
  {
    value: 'deposits',
    label: t('common.deposits'),
    content: t('page.bank.slug.tabs_content.deposits')
  },
  {
    value: 'cards',
    label: t('common.bank_cards'),
    content: t('page.bank.slug.tabs_content.cards')
  }
])

const filteredTabs = computed(() =>
  tabs.value.filter((tab) => {
    if (tab.value === 'credits') return hasCredits.value === true
    if (tab.value === 'deposits') return hasDeposits.value === true
    if (tab.value === 'cards') return hasCards.value === true
    return false
  })
)

watchEffect(() => {
  if (!isTabsInitialized.value) {
    const creditsLoaded = hasCredits.value !== null
    const depositsLoaded = hasDeposits.value !== null
    const cardsLoaded = hasCards.value !== null

    if (depositsLoaded && creditsLoaded && cardsLoaded) {
      const firstAvailableTab = filteredTabs.value[0]?.value
      if (firstAvailableTab) {
        activeTab.value = firstAvailableTab
      }
      isTabsInitialized.value = true
    }
  }
})

const currentBankDetailsList = computed(() => getBankDetailsList(bank.value))
const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))

const shouldShowMapSection = computed(() => {
  if (!bank.value) return false
  if (bank.value.is_nbkr) return false

  const { branches_count = 0, atms_count = 0, terminals_count = 0 } = bank.value

  return branches_count > 0 || atms_count > 0 || terminals_count > 0
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetRateAlertSidebar
              v-if="bank && !bank.is_nbkr"
              :bank-id="bank.id"
              :bank-name="bank.title"
              :all-organizations-rates="allOrganizationsRates"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <SidebarBlock
              v-if="bank && bank.is_nbkr"
              variant="white"
              :heading-text="$t('page.bank.slug.sidebar_nbkr_rates')"
            >
              <WidgetSidebarRates is-nbkr />
            </SidebarBlock>
            <SidebarBlock
              v-if="bank && bank.is_nbkr"
              variant="white"
              :heading-text="$t('page.bank.slug.sidebar_gold_price')"
            >
              <WidgetSidebarGoldPrice />
            </SidebarBlock>
            <WidgetSelectCreditType
              v-if="bank && !bank.is_nbkr"
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBank :bank="bank" />

          <section v-if="bank && bank.id && bank.status === 'active'">
            <TransitionBlock>
              <ContentBlock v-show="hasDeposits || hasCredits || hasCards">
                <Tabs v-model="activeTab" class="w-full space-y-5">
                  <header class="space-y-5 lg:flex lg:items-center lg:justify-between lg:space-y-0">
                    <Heading tag="h2" class="h4">{{ $t('page.bank.slug.popular_offers') }}</Heading>

                    <TabsList class="flex-wrap">
                      <TabsTrigger v-for="tab in filteredTabs" :key="tab.value" :value="tab.value">
                        {{ tab.label }}
                      </TabsTrigger>
                    </TabsList>
                  </header>

                  <div v-show="activeTab === 'credits'">
                    <WidgetCreditsList
                      :hide-pagination="true"
                      :show-types="true"
                      :query-params="{
                        per_page: '10',
                        'filter[organization_id]': bank.id.toString(),
                        'filter[organization_type]': 'bank',
                        'filter[position]': 'banks'
                      }"
                      @loaded="hasCredits = $event"
                    />
                  </div>

                  <div v-show="activeTab === 'deposits'">
                    <WidgetDepositsList
                      :hide-pagination="true"
                      :show-types="true"
                      :query-params="{
                        per_page: '10',
                        'filter[organization_id]': bank.id.toString(),
                        'filter[organization_type]': 'bank',
                        'filter[position]': 'banks'
                      }"
                      @loaded="hasDeposits = $event"
                    />
                  </div>

                  <div v-show="activeTab === 'cards'">
                    <WidgetCardsList
                      :hide-organization-details="true"
                      :hide-pagination="true"
                      :query-params="{
                        per_page: '10',
                        'filter[organization_id]': bank.id.toString(),
                        'filter[organization_type]': 'bank',
                        'filter[position]': 'banks'
                      }"
                      @loaded="hasCards = $event"
                    />
                  </div>
                </Tabs>
              </ContentBlock>
            </TransitionBlock>
          </section>

          <section v-if="bank && bank.staff && bank.staff.length">
            <ContentBlock
              :heading-text="$t('page.bank.slug.board_heading')"
              :heading-tag="'h2'"
              heading-class="h4"
            >
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <WidgetStaff
                  v-for="(item, i) in bank.staff"
                  :key="i"
                  variant="white"
                  v-bind="item"
                />
              </div>
            </ContentBlock>
          </section>

          <section v-if="shouldShowMapSection">
            <WidgetBankPageShowOnMap />
          </section>

          <section
            v-if="currentBankDetailsList && currentBankDetailsList.length && bank && !bank.is_nbkr"
          >
            <WidgetBankPageDetails :details="currentBankDetailsList" :slug="bank.slug" />
          </section>

          <section
            v-if="bank && bank.permissions.is_credit_request_allowed && bank.status === 'active'"
          >
            <FeatureCreditFormRequest
              :organization="bank"
              variant="white"
              :heading-text="$t('common.credit_request_heading')"
              heading-tag="h2"
              heading-class="h4"
            />
          </section>

          <section v-if="bank && bank.is_nbkr">
            <ContentBlock
              :heading-text="$t('page.bank.slug.nbkr_rates_heading')"
              variant="white"
              class="mt-6"
              heading-tag="h2"
              heading-class="h4"
            >
              <WidgetRatesNbkr />
            </ContentBlock>
          </section>

          <section v-if="bank && bank.is_nbkr">
            <ContentBlock variant="white">
              <WidgetBankPageRatesChartNbkr :id="bank.id" :title="bank.title" />
            </ContentBlock>
          </section>

          <WidgetNewsSlider
            v-if="bank && bank.is_nbkr"
            :heading="$t('page.bank.slug.nbkr_news_heading')"
            hide-image
            hide-add-to-favorites
            :slides-per-view-md="3"
            :slides-per-view-sm="3"
            :query-params="{
              per_page: '10',
              'filter[tag]': 'nb-kr,nbkr',
              'filter[type]': 'news'
            }"
          />

          <section v-if="bank && bank.applications && bank.applications.length">
            <ContentBlock
              :heading-text="$t('page.bank.slug.mobile_apps_heading')"
              variant="white"
              heading-tag="h2"
              heading-class="h4"
            >
              <!-- The slot can re-render while leaving the page, after bank data is gone -->
              <WidgetApplications :applications="bank?.applications ?? []" variant="white" />
            </ContentBlock>
          </section>

          <WidgetRating
            v-if="bank && !bank.is_nbkr && config.public.widgetRatingEnabled"
            :entity="bank"
            entity-type="bank"
            :heading-text="$t('page.bank.slug.rating_heading', { title: bank.title })"
          />

          <section
            v-if="
              bank &&
              bank.permissions &&
              bank.permissions.is_contact_request_allowed &&
              bank.status === 'active'
            "
          >
            <FeatureBankPageFormSupport
              :id="bank.id"
              type="bank"
              :heading-text="$t('page.bank.slug.support_heading', { bankName: bank.title })"
              heading-tag="h2"
              heading-class="h4"
              variant="white"
            />
          </section>

          <section v-if="bank">
            <WidgetComments commentable-type="bank" :commentable-id="bank.id" />
          </section>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
